import { METRICS } from '@/pages/recommendation-lab/data';
import type { RecommendationEnvironmentFormValue } from '@/pages/recommendation-lab/environment';
import { normalizeRankingPercentages } from '@/pages/recommendation-lab/rankingPolicy';
import type { SavedValidationCase } from '@/pages/recommendation-lab/validation';

const CASES_STORAGE_KEY = 'recommendation-lab-cases-client-parity-v1';
const LEGACY_CASES_STORAGE_KEYS = ['recommendation-lab-cases-v3', 'recommendation-lab-cases-v2'];

type PersistedRecommendationEnvironment = Omit<RecommendationEnvironmentFormValue, 'airQualityIndex'> &
  Partial<Pick<RecommendationEnvironmentFormValue, 'airQualityIndex'>>;

type PersistedSavedValidationCase = Omit<SavedValidationCase, 'environment' | 'rankingPercentages'> & {
  environment: PersistedRecommendationEnvironment;
  rankingPercentages?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isReplayBaseline(candidate: unknown): boolean {
  if (!isRecord(candidate) || typeof candidate['resultId'] !== 'string' || !isRecord(candidate['recommendations'])) {
    return false;
  }
  const steps = candidate['recommendations']['steps'];
  return (
    Array.isArray(steps) &&
    steps.every(
      (step) =>
        isRecord(step) &&
        typeof step['stepId'] === 'string' &&
        Array.isArray(step['products']) &&
        step['products'].every((product) => isRecord(product) && typeof product['productId'] === 'number'),
    )
  );
}

function isSavedCase(candidate: unknown): candidate is PersistedSavedValidationCase {
  if (!isRecord(candidate) || !isRecord(candidate['metricScores']) || !isRecord(candidate['environment'])) {
    return false;
  }
  const metricScores = candidate['metricScores'];
  const environment = candidate['environment'];
  const surveyAnswers = candidate['surveyAnswers'];
  const savedAt = candidate['savedAt'];
  const season = environment['season'];
  return (
    candidate['schemaVersion'] === 'client-parity-v1' &&
    typeof candidate['surveyVersion'] === 'string' &&
    typeof candidate['name'] === 'string' &&
    typeof savedAt === 'string' &&
    Number.isFinite(Date.parse(savedAt)) &&
    METRICS.every(
      (metric) => typeof metricScores[metric.key] === 'number' && Number.isFinite(metricScores[metric.key]),
    ) &&
    isRecord(surveyAnswers) &&
    Object.values(surveyAnswers).every(
      (values) => Array.isArray(values) && values.every((value) => typeof value === 'string'),
    ) &&
    ['temperatureC', 'humidityPercent', 'uvIndex'].every((key) => typeof environment[key] === 'string') &&
    (environment['airQualityIndex'] === undefined || typeof environment['airQualityIndex'] === 'string') &&
    (season === '' || season === 'spring' || season === 'summer' || season === 'autumn' || season === 'winter') &&
    (candidate['baselineResult'] === undefined || isReplayBaseline(candidate['baselineResult']))
  );
}

export function loadSavedCases(): SavedValidationCase[] {
  try {
    const raw = localStorage.getItem(CASES_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter(isSavedCase).map((saved) => {
          const { rankingPercentages: storedRankingPercentages, ...savedWithoutRankingPercentages } = saved;
          const rankingPercentages = normalizeRankingPercentages(storedRankingPercentages);
          return {
            ...savedWithoutRankingPercentages,
            environment: {
              temperatureC: savedWithoutRankingPercentages.environment.temperatureC,
              humidityPercent: savedWithoutRankingPercentages.environment.humidityPercent,
              uvIndex: savedWithoutRankingPercentages.environment.uvIndex,
              season: savedWithoutRankingPercentages.environment.season,
              airQualityIndex: savedWithoutRankingPercentages.environment.airQualityIndex ?? '',
            },
            ...(rankingPercentages ? { rankingPercentages } : {}),
          };
        })
      : [];
  } catch {
    return [];
  }
}

export function persistSavedCases(cases: SavedValidationCase[]): void {
  localStorage.setItem(CASES_STORAGE_KEY, JSON.stringify(cases));
}

export function hasLegacySavedCases(): boolean {
  return LEGACY_CASES_STORAGE_KEYS.some((key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        return false;
      }
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length > 0;
    } catch {
      return false;
    }
  });
}
